import { StoreItemCategory, StorePresalePackType } from '../entity_datas/StoreEnums.ts';
import { BaseStoreItem, GIFT_MEMBERSHIP_EXPIRY_YEARS } from './BaseStoreItem.ts';
import { ArrayUtils } from '../utils/ArrayUtils.ts';
import { ColonistMembershipProduct } from './ColonistMembershipProduct.ts';
import { EnumUtils, TimeUtils } from '@colonist/utils';
import { PlayerColor } from '@colonist/game-states';
const loopEnums = EnumUtils.loopEnums;
import { ColonistMembershipPerkType } from '@colonist/lobby-types';
import { StoreItemController } from './StoreItemController.ts';
export class UserItems {
    static deserialize(data) {
        const items = new UserItems();
        items.colonistCoins = data.colonistCoins;
        items.presalePacks = data.presalePacks;
        items.expansions = data.expansions;
        items.maps = data.maps;
        items.icons = data.icons;
        items.colors = data.colors;
        items.giftedMemberships = data.giftedMemberships;
        items.membership = data.membership;
        items.membershipPending = data.membershipPending;
        items.membershipEndDate = data.membershipEndDate;
        items.membershipPaymentMethod = data.membershipPaymentMethod;
        return items;
    }
    hasAccessToStoreMap(mapType) {
        if (mapType.length == 0) return true;
        for (const map of this.maps){
            if (ArrayUtils.elementInArray(mapType, map.type)) return true;
        }
        return false;
    }
    hasAccessToExpansion(expansionType) {
        if (expansionType.length == 0) return true;
        for (const expansion of this.expansions){
            if (ArrayUtils.elementInArray(expansionType, expansion.type)) return true;
        }
        return false;
    }
    hasAccessToPremiumMap(mapData) {
        if (this.isPerkAvailable(ColonistMembershipPerkType.AllMaps)) return true;
        if (!mapData.isMapPremium()) return true;
        const isMapSelectionRestrictedByPremiumMode = mapData.isMapRestrictedByPremiumGameMode() && !this.hasAccessToExpansion(mapData.premiumExpansionAccess);
        if (isMapSelectionRestrictedByPremiumMode) return false;
        const isMapSelectionRestrictedByPremiumMap = mapData.isMapRestrictedByPremiumMap() && !this.hasAccessToStoreMap(mapData.premiumMapAccess);
        if (isMapSelectionRestrictedByPremiumMap) return false;
        return true;
    }
    hasAccessToPremiumMode(gameModeData) {
        if (this.isPerkAvailable(ColonistMembershipPerkType.AllExpansions)) return true;
        if (!gameModeData.isGameModePremium()) return true;
        return this.hasAccessToExpansion(gameModeData.premiumExpansionAccess);
    }
    hasAccessToColor(color) {
        if (this.isPerkAvailable(ColonistMembershipPerkType.AllColors)) return true;
        const freeColors = [
            PlayerColor.Red,
            PlayerColor.Blue,
            PlayerColor.Green,
            PlayerColor.Orange
        ];
        if (freeColors.includes(color)) return true;
        for (const colorItem of this.colors){
            if (BaseStoreItem.getStorePlayerColorOfPlayerColor(color) == colorItem.type) return true;
        }
        return false;
    }
    hasAccessToNoTrolls() {
        if (this.isNoTrollsAvailable) return true;
        return false;
    }
    hasMembership() {
        return this.membership != undefined;
    }
    // ====================================================================================================================================
    // Presale Packs
    // ====================================================================================================================================
    hasFoundersPack() {
        const index = this.presalePacks.findIndex((pack)=>pack.type == StorePresalePackType.FounderPack && pack.category == StoreItemCategory.PresalePack);
        return index >= 0;
    }
    // ====================================================================================================================================
    // Items
    // ====================================================================================================================================
    addGiftedMembership(membership) {
        if (TimeUtils.addYears(membership.giftedAt, GIFT_MEMBERSHIP_EXPIRY_YEARS) < new Date()) return;
        this.giftedMemberships.push(membership);
    }
    addStoreItem(category, type) {
        const item = StoreItemController.getItemDetail(category, type);
        if (!item) return;
        switch(item.category){
            case StoreItemCategory.PresalePack:
                this.presalePacks.push(item);
                return;
            case StoreItemCategory.Expansion:
                this.expansions.push(item);
                return;
            case StoreItemCategory.Map:
                this.maps.push(item);
                return;
            case StoreItemCategory.Icon:
                this.icons.push(item);
                return;
            case StoreItemCategory.Color:
                this.colors.push(item);
                return;
            case StoreItemCategory.Consumable:
                return;
            case StoreItemCategory.Membership:
                return;
        }
    }
    getAvailableColors() {
        const result = [];
        loopEnums(PlayerColor, (_, color)=>{
            if (this.hasAccessToColor(color)) {
                result.push(color);
            }
        });
        return result;
    }
    // ====================================================================================================================================
    // Membership
    // ====================================================================================================================================
    isPerkAvailable(perk) {
        if (this.membership == undefined) return false;
        const membershipType = ColonistMembershipProduct.getMembershipProduct(this.membership);
        const hasPerk = membershipType.perks.filter((perkItem)=>perkItem.type == perk).length > 0;
        return hasPerk;
    }
    get isNoTrollsAvailable() {
        if (this.membership == undefined) return false;
        return true;
    }
    getCurrentMembership() {
        const currentMembership = this.membership;
        if (currentMembership == undefined) return undefined;
        const membershipProduct = ColonistMembershipProduct.getMembershipProduct(currentMembership);
        return {
            type: currentMembership,
            image: membershipProduct.lobbyImage,
            pending: this.membershipPending,
            title: membershipProduct.membershipTitle
        };
    }
    getItems() {
        return [
            ...this.presalePacks,
            ...this.expansions,
            ...this.maps,
            ...this.icons,
            ...this.colors
        ];
    }
    hideAds() {
        if (this.hasMembership()) return true;
        if (this.presalePacks.length > 0) return true;
        return false;
    }
    constructor(){
        this.colonistCoins = 0;
        this.presalePacks = [];
        this.expansions = [];
        this.maps = [];
        this.icons = [];
        this.colors = [];
        this.giftedMemberships = [];
        this.membership = undefined;
        this.membershipPending = false;
        this.membershipEndDate = undefined;
        this.membershipPaymentMethod = undefined;
    }
}
